"use client";

import { c as _c } from "react/compiler-runtime";
import { MapPinIcon } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { GeocodedAddressFieldErrors, GeocodedAddressValue } from "@/src/components/address/types";
import { Button } from "@/src/components/shadcn/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/src/components/shadcn/ui/command";
import { Input } from "@/src/components/shadcn/ui/input";
import { Label } from "@/src/components/shadcn/ui/label";
import { Popover, PopoverAnchor, PopoverContent } from "@/src/components/shadcn/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { useAddressGeocodingSearch } from "@/src/hooks/use-address-geocoding-search";
import type { AddressGeocodingClient } from "@/src/lib/geocoding-client";
import { buildAddressFromPlaceDetails } from "@/src/lib/geocoding-utils";
import { cn } from "@/src/lib/shadcn-utils";
import { stateOptions, type USState } from "@/src/lib/us-states";
function OptionalHint() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="ml-1 font-normal text-muted-foreground">(optional)</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function FieldError(t0) {
  const $ = _c(2);
  const {
    message
  } = t0;
  if (!message) {
    return null;
  }
  let t1;
  if ($[0] !== message) {
    t1 = <p className="text-sm font-medium text-destructive">{message}</p>;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function FieldLabel(t0) {
  const $ = _c(6);
  const {
    children,
    htmlFor,
    hasError
  } = t0;
  const t1 = hasError && "text-destructive";
  let t2;
  if ($[0] !== t1) {
    t2 = cn(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== htmlFor || $[4] !== t2) {
    t3 = <Label className={t2} htmlFor={htmlFor}>{children}</Label>;
    $[2] = children;
    $[3] = htmlFor;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function GeocodedAddressFields({
  value,
  onChange,
  geocodingClient,
  biasStateCode,
  allowManualEntry = true,
  inputClassName,
  errors,
  idPrefix = "address"
}: {
  value: GeocodedAddressValue;
  onChange: (value: GeocodedAddressValue) => void;
  geocodingClient: AddressGeocodingClient;
  /** Bias autocomplete results toward a state when the tenant primarily serves one market. */
  biasStateCode?: USState;
  allowManualEntry?: boolean;
  inputClassName?: string;
  errors?: GeocodedAddressFieldErrors;
  idPrefix?: string;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(value.line1);
  const [manualEntry, setManualEntry] = useState(!value.placeId && Boolean(value.line1));
  const [selectedSuggestionId, setSelectedSuggestionId] = useState("");
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const committedSearchValue = useRef(value.line1);
  const showAdditionalFields = manualEntry || Boolean(value.placeId);
  const {
    suggestions,
    isLoading,
    error: searchError,
    shouldShowSuggestions
  } = useAddressGeocodingSearch({
    searchTerm: manualEntry ? "" : searchValue,
    client: geocodingClient,
    stateCode: biasStateCode,
    enabled: !manualEntry
  });
  useEffect(() => {
    setSearchValue(value.line1);
    committedSearchValue.current = value.line1;
  }, [value.line1]);
  useEffect(() => {
    if (suggestions.length > 0) {
      setSelectedSuggestionId(suggestions[0]?.placeId ?? "");
    } else {
      setSelectedSuggestionId("");
    }
  }, [suggestions]);
  useEffect(() => {
    const hasHiddenFieldError = errors?.city || errors?.state || errors?.postalCode;
    if (value.line1 && !value.placeId && hasHiddenFieldError) {
      setManualEntry(true);
    }
  }, [errors?.city, errors?.postalCode, errors?.state, value.line1, value.placeId]);
  const updateField = <K extends keyof GeocodedAddressValue,>(key: K, next: GeocodedAddressValue[K]) => {
    onChange({
      ...value,
      [key]: next
    });
  };
  const handleSuggestionSelect = async (placeId: string, label: string) => {
    setIsResolvingPlace(true);
    setResolveError(null);
    try {
      const placeDetails = await geocodingClient.fetchPlaceDetails(placeId);
      const resolved = buildAddressFromPlaceDetails(placeDetails, placeId);
      onChange({
        ...resolved,
        line2: value.line2 || resolved.line2
      });
      setSearchValue(resolved.line1 || label);
      committedSearchValue.current = resolved.line1 || label;
      setManualEntry(false);
      setSearchOpen(false);
    } catch {
      setResolveError("We couldn't verify that address. Try another result or enter it manually.");
    } finally {
      setIsResolvingPlace(false);
    }
  };
  const handleSearchChange = (next_0: string) => {
    setSearchValue(next_0);
    setResolveError(null);
    onChange({
      ...value,
      line1: next_0,
      placeId: next_0 === value.line1 ? value.placeId : null
    });
    if (!searchOpen) {
      setSearchOpen(true);
    }
  };
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchOpen || event.key !== "Enter" || !selectedSuggestionId) {
      return;
    }
    event.preventDefault();
    const selected = suggestions.find(suggestion => suggestion.placeId === selectedSuggestionId);
    if (selected) {
      void handleSuggestionSelect(selected.placeId, selected.label);
    }
  };
  const showSuggestionPanel = Boolean(!manualEntry && searchOpen && shouldShowSuggestions(searchValue) && (isLoading || suggestions.length > 0 || searchError));
  return <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel htmlFor={`${idPrefix}-line1`} hasError={!!errors?.line1}>
          {showAdditionalFields ? "Street address" : "Address"}
        </FieldLabel>
        <div className="relative">
          <Popover open={showSuggestionPanel} onOpenChange={open => {
          if (!open) {
            setSearchValue(committedSearchValue.current);
          }
          setSearchOpen(open);
        }}>
            <PopoverAnchor asChild>
              <Input id={`${idPrefix}-line1`} autoComplete="address-line1" placeholder="Start typing your address" maxLength={120} className={inputClassName} value={searchValue} onChange={event_0 => handleSearchChange(event_0.target.value)} onFocus={() => setSearchOpen(true)} onKeyDown={handleSearchKeyDown} aria-invalid={!!errors?.line1} />
            </PopoverAnchor>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-muted/50 p-0 shadow-sm" align="start" side="bottom" onOpenAutoFocus={event_1 => event_1.preventDefault()}>
              <Command shouldFilter={false} value={selectedSuggestionId} onValueChange={setSelectedSuggestionId}>
                <CommandList>
                  {isLoading ? <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Searching addresses...
                    </div> : searchError ? <div className="px-3 py-2 text-sm text-muted-foreground">{searchError}</div> : <CommandGroup heading="Suggested addresses">
                      {suggestions.map(suggestion_0 => <CommandItem key={suggestion_0.placeId} value={suggestion_0.placeId} onSelect={() => void handleSuggestionSelect(suggestion_0.placeId, suggestion_0.label)} className="flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{suggestion_0.label}</span>
                        </CommandItem>)}
                    </CommandGroup>}
                  {!isLoading && !searchError && suggestions.length === 0 ? <CommandEmpty>No matching addresses.</CommandEmpty> : null}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {isResolvingPlace ? <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Verifying address...
            </div> : null}
          {resolveError ? <p className="mt-1 text-sm text-destructive">{resolveError}</p> : null}
        </div>
        <FieldError message={errors?.line1} />
        {allowManualEntry && (manualEntry || !value.placeId) ? <Button type="button" variant="ghost" size="sm" className="-ml-3 font-normal text-muted-foreground" onClick={() => {
        setManualEntry(current => !current);
        setResolveError(null);
        if (!manualEntry) {
          onChange({
            ...value,
            placeId: null
          });
        }
      }}>
            {manualEntry ? "Search for my address instead" : "Enter address manually"}
          </Button> : null}
      </div>

      {showAdditionalFields ? <>
          <div className="space-y-2">
            <FieldLabel htmlFor={`${idPrefix}-line2`} hasError={!!errors?.line2}>
              Apartment, suite, etc.
              <OptionalHint />
            </FieldLabel>
            <Input id={`${idPrefix}-line2`} autoComplete="address-line2" placeholder="Apt 4B" maxLength={120} className={inputClassName} value={value.line2} onChange={event_2 => updateField("line2", event_2.target.value)} aria-invalid={!!errors?.line2} />
            <FieldError message={errors?.line2} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <FieldLabel htmlFor={`${idPrefix}-city`} hasError={!!errors?.city}>
                City
              </FieldLabel>
              <Input id={`${idPrefix}-city`} autoComplete="address-level2" placeholder="Nashville" maxLength={80} className={inputClassName} value={value.city} onChange={event_3 => updateField("city", event_3.target.value)} aria-invalid={!!errors?.city} />
              <FieldError message={errors?.city} />
            </div>

            <div className="space-y-2">
              <FieldLabel hasError={!!errors?.state}>State</FieldLabel>
              <Select value={value.state} onValueChange={next_1 => updateField("state", next_1)}>
                <SelectTrigger className={inputClassName} aria-invalid={!!errors?.state}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {stateOptions.map(option => <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FieldError message={errors?.state} />
            </div>

            <div className="space-y-2">
              <FieldLabel htmlFor={`${idPrefix}-postalCode`} hasError={!!errors?.postalCode}>
                ZIP code
              </FieldLabel>
              <Input id={`${idPrefix}-postalCode`} inputMode="numeric" autoComplete="postal-code" placeholder="37201" maxLength={10} className={inputClassName} value={value.postalCode} onChange={event_4 => updateField("postalCode", event_4.target.value)} aria-invalid={!!errors?.postalCode} />
              <FieldError message={errors?.postalCode} />
            </div>
          </div>
        </> : null}
    </div>;
}